import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, Search, X as XIcon, AlertTriangle, CheckSquare, Square, UserCog } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { usePageMeta } from "../../components/Layout";
import { BUDGET_OPTIONS } from "../../lib/enums";

// B1 Projects landing — Workstage grouping is structural (not a filter).
// Default Project State = Active. Row-click routes to the temporary
// compatible project-detail destination (Enquiry detail) until B2
// replaces it (per §5 last paragraph).

// Budget enum → compact display label for the Projects list only
// (canonical verbose labels in `BUDGET_OPTIONS[].label` are still used
// in the enquiry form, filter dropdown, and workspace surfaces).
const BUDGET_DISPLAY = BUDGET_OPTIONS.reduce((acc, o) => {
    acc[o.value] = o.label_compact || o.label;
    return acc;
}, {});

const WORKSTAGE_ORDER = [
    { num: 1, name: "Enquiry & Qualification" },
    { num: 2, name: "Feasibility" },
    { num: 3, name: "Free Design Onboarding" },
    { num: 4, name: "Free Design Package" },
    { num: 5, name: "Project Transition" },
    { num: 6, name: "Paid Services" },
    { num: 7, name: "Post Completion" },
];

const DEFAULT_STATE = "Active";

function Pill({ children, tone = "neutral" }) {
    const toneMap = {
        active: "bg-[#E8F5E9] text-[#2E7D32]",
        hold: "bg-[#FFF3E0] text-[#E65100]",
        closed: "bg-[#EEE] text-[#616161]",
        high: "bg-[#E3F2FD] text-[#1565C0]",
        medium: "bg-[#F5F5F5] text-[#616161]",
        low: "bg-[#F5F5F5] text-[#666]",
        neutral: "bg-[#F5F5F5] text-[#666]",
    };
    return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[12px] font-medium ${toneMap[tone] || toneMap.neutral}`}>{children}</span>;
}

function SourceBadge({ source, detail }) {
    if (!source) return <span className="text-[#666] text-[12px]">—</span>;
    const kv = Object.entries(detail || {}).filter(([, v]) => v !== null && v !== undefined && v !== "");
    return (
        <span className="relative inline-block group" data-testid="project-source">
            <span className="bg-[#FDF4F2] text-terracotta border border-terracotta/20 px-2 py-1 rounded text-[12px] font-semibold cursor-default select-none">
                {source}
            </span>
            {kv.length > 0 && (
                <span className="pointer-events-none group-hover:pointer-events-auto absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-[360px] max-h-[400px] overflow-y-auto bg-charcoal text-offwhite rounded-lg p-4 text-[12px] leading-relaxed opacity-0 invisible group-hover:opacity-100 group-hover:visible transition z-40 whitespace-normal shadow-xl">
                    <div className="font-manrope font-semibold text-[13px] mb-2 uppercase tracking-wide text-white/70">Stored attribution</div>
                    {kv.map(([k, v]) => (
                        <div key={k} className="grid grid-cols-[130px,1fr] gap-2 mb-1">
                            <span className="text-white/50">{k}</span>
                            <span className="text-white select-text break-all">{String(v)}</span>
                        </div>
                    ))}
                </span>
            )}
        </span>
    );
}

function NextItemCell({ item, testid }) {
    if (!item) {
        return <span className="text-[#666]" data-testid={testid}>—</span>;
    }
    return (
        <div className="max-w-[220px]" data-testid={testid}>
            <div className="text-[13px] text-charcoal truncate"
                 title={item.title || ""}>
                {item.title || "Action"}
            </div>
            <div className={`text-[12px] mt-1 ${item.overdue ? "text-terracotta font-semibold" : "text-[#666]"}`}>
                {fmtDate(item.due_at)}
                {item.overdue && <span className="ml-1 uppercase tracking-wide font-bold text-[11px]">Overdue</span>}
            </div>
        </div>
    );
}

function AttentionCell({ a }) {
    if (!a) return <span className="text-[#666]">—</span>;
    return (
        <span className="inline-flex items-center gap-1 text-terracotta font-semibold text-[13px]" data-testid="project-attention">
            <AlertTriangle className="w-3.5 h-3.5" />{a.title}
        </span>
    );
}

function fmtDate(iso) {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString(undefined, { day: "2-digit", month: "short" }); }
    catch { return "—"; }
}

// §Projects WS1/WS2 Tweak — two-line "Created At" cell formatter.
//   Uses Intl.DateTimeFormat("en-GB") so the date reads
//   "DD MMM YYYY" and the time reads "HH:mm" (24-hour).  Returns
//   `null` when the timestamp is missing so the caller can render
//   an em-dash placeholder inline.
const _CREATED_AT_DATE_FMT = new Intl.DateTimeFormat("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
});
const _CREATED_AT_TIME_FMT = new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit", minute: "2-digit", hour12: false,
});
function fmtCreatedAt(iso) {
    if (!iso) return null;
    try {
        const d = new Date(iso);
        return { date: _CREATED_AT_DATE_FMT.format(d),
                 time: _CREATED_AT_TIME_FMT.format(d) };
    } catch { return null; }
}

function fmtRel(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    const diffH = (Date.now() - d.getTime()) / 36e5;
    if (diffH < 1) return "just now";
    if (diffH < 24) return `${Math.floor(diffH)}h ago`;
    if (diffH < 24 * 7) return `${Math.floor(diffH / 24)}d ago`;
    return d.toLocaleDateString(undefined, { day: "2-digit", month: "short" });
}

// D5 — inline Owner selector.  Compact cell control: click to open a
// small popover of eligible platform users; delegates to
// POST /admin/projects/{project_id}/owner which itself delegates to
// `stage5_ownership.assign_project_owner` (never direct $set).
function OwnerCell({ project, admins, onOwnerChanged }) {
    const [open, setOpen] = useState(false);
    const [saving, setSaving] = useState(false);

    const label = project.owner_display_name
        || (project.owner_id ? project.owner_id.slice(0, 8) + "…" : "—");

    const assign = async (newOwnerId) => {
        setSaving(true);
        try {
            const r = await api.post(`/admin/projects/${project.id}/owner`, {
                new_owner_id: newOwnerId,
                expected_owner_version: project.owner_version || 0,
                reason: "reassigned via Projects table",
            });
            onOwnerChanged(project.id, r.data);
            toast.success("Owner assigned");
            setOpen(false);
        } catch (e) {
            const detail = e?.response?.data?.detail || "Could not assign owner";
            toast.error(detail);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="relative" onClick={(e) => e.stopPropagation()}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                data-testid={`owner-cell-${project.id}`}
                className={`inline-flex items-center gap-1 px-2 py-1 rounded border text-[12px] transition ${
                    project.owner_id
                        ? "border-[#E5E2DC] bg-white text-charcoal hover:border-terracotta"
                        : "border-dashed border-[#B71C1C]/40 bg-[#FDECEA]/40 text-[#B71C1C] hover:bg-[#FDECEA]"
                }`}
            >
                <UserCog className="w-3.5 h-3.5" />
                <span className="max-w-[140px] truncate" data-testid={`owner-cell-label-${project.id}`}>
                    {label}
                </span>
                <ChevronDown className="w-3 h-3 opacity-60" />
            </button>
            {open && (
                <div
                    className="absolute z-30 mt-1 w-[240px] bg-white border border-[#E5E2DC] rounded-lg shadow-lg p-1"
                    data-testid={`owner-popover-${project.id}`}
                >
                    {admins.length === 0 && (
                        <div className="px-3 py-2 text-[12px] text-[#999]">No eligible admins</div>
                    )}
                    {admins.map((a) => {
                        const selected = a.id === project.owner_id;
                        return (
                            <button
                                key={a.id}
                                type="button"
                                onClick={() => !selected && assign(a.id)}
                                disabled={saving || selected}
                                data-testid={`owner-option-${project.id}-${a.id}`}
                                className={`w-full text-left px-3 py-2 text-[13px] rounded transition ${
                                    selected
                                        ? "bg-[#F5F5F5] text-[#666] cursor-default"
                                        : "hover:bg-[#FAFAFA] text-charcoal"
                                }`}
                            >
                                <div className="font-semibold">{a.display_name || a.email}</div>
                                <div className="text-[11px] text-[#666]">{a.email}</div>
                                {selected && <div className="text-[10px] text-[#2E7D32] mt-0.5">Currently assigned</div>}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

export default function ProjectsLanding() {
    usePageMeta({ title: "Projects | Studio G4 Admin" });
    const navigate = useNavigate();
    const [data, setData] = useState({ sections: [], summary: {}, total_visible: 0 });
    const [globalSummary, setGlobalSummary] = useState({});
    const [loading, setLoading] = useState(true);
    const [state, setState] = useState(DEFAULT_STATE);
    const [owner, setOwner] = useState([]);
    const [attention, setAttention] = useState([]);
    const [quality, setQuality] = useState([]);
    const [source, setSource] = useState([]);
    const [budget, setBudget] = useState([]);
    const [ptype, setPtype] = useState([]);
    const [closureOutcome, setClosureOutcome] = useState([]);
    const [outcomeOptions, setOutcomeOptions] = useState([]);
    // V1 §6 — First-Contact filter.  Radio: "" (any) | "none" | "made".
    const [firstContact, setFirstContact] = useState("");
    const [q, setQ] = useState("");
    const [shortcut, setShortcut] = useState("");
    const [collapsed, setCollapsed] = useState({});

    // D5 — Owner assignment state.
    const [admins, setAdmins] = useState([]);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [bulkOwnerId, setBulkOwnerId] = useState("");
    const [bulkResult, setBulkResult] = useState(null);
    const [bulkRunning, setBulkRunning] = useState(false);

    // Fetch eligible platform users once (small list).
    useEffect(() => {
        let cancelled = false;
        api.get("/admin/users").then((r) => {
            if (cancelled) return;
            setAdmins((r.data?.items || []).filter((u) => u.is_active !== false));
        }).catch(() => setAdmins([]));
        return () => { cancelled = true; };
    }, []);

    const closedSelected = state === "Closed";

    // Fetch the authoritative Closure Outcome catalogue once. Values are
    // NEVER invented on the client — they come from the curated
    // `closure_outcomes` collection (A2.2b) via the admin catalogue
    // endpoint. If the endpoint is unavailable, the list stays empty
    // and the group renders its "no configured outcomes" hint.
    useEffect(() => {
        let cancelled = false;
        api.get("/admin/closure_outcomes/active")
            .then(r => { if (!cancelled) setOutcomeOptions(r.data?.items || []); })
            .catch(() => { if (!cancelled) setOutcomeOptions([]); });
        return () => { cancelled = true; };
    }, []);

    // Global operational summary counts — stable regardless of any
    // sidebar filter selection (§5). Fetched once on mount so that
    // applying filters never shrinks the card numbers.
    useEffect(() => {
        let cancelled = false;
        api.get("/admin/projects/summary")
            .then(r => { if (!cancelled) setGlobalSummary(r.data || {}); })
            .catch(() => { if (!cancelled) setGlobalSummary({}); });
        return () => { cancelled = true; };
    }, []);

    // §5 (revised) — Closure Outcome selection is only meaningful when
    // Project State === "Closed". Any transition away from Closed
    // (including clearing Project State entirely) must hide Closure
    // Outcome AND clear any active Closure Outcome selection.
    useEffect(() => {
        if (!closedSelected && closureOutcome.length > 0) {
            setClosureOutcome([]);
        }
    }, [closedSelected, closureOutcome.length]);

    const query = useMemo(() => {
        const p = new URLSearchParams();
        // Project State is now single-select (or none). UI emits at most
        // one `project_state` value; backend `_expand(...)` tolerance for
        // repeated / comma-separated shapes is preserved unchanged.
        if (state) p.append("project_state", state);
        for (const v of owner) p.append("owner", v);
        for (const v of attention) p.append("attention", v);
        for (const v of quality) p.append("quality", v);
        for (const v of source) p.append("source", v);
        for (const v of budget) p.append("budget", v);
        for (const v of ptype) p.append("project_type", v);
        // Closure Outcome only sent when Closed is the selected Project
        // State (§5 revised). Backend safely ignores otherwise, but
        // keeping the URL clean helps introspection.
        if (closedSelected) {
            for (const v of closureOutcome) p.append("closure_outcome", v);
        }
        if (firstContact) p.set("first_contact", firstContact);
        if (q) p.set("q", q);
        if (shortcut) p.set("shortcut", shortcut);
        return p.toString();
    }, [state, owner, attention, quality, source, budget, ptype,
        closureOutcome, closedSelected, firstContact, q, shortcut]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        api.get(`/admin/projects?${query}`).then(r => { if (!cancelled) setData(r.data); }).finally(() => !cancelled && setLoading(false));
        return () => { cancelled = true; };
    }, [query]);

    const clearFilters = () => {
        setState(DEFAULT_STATE); setOwner([]); setAttention([]); setQuality([]);
        setSource([]); setBudget([]); setPtype([]); setClosureOutcome([]);
        setFirstContact("");
        setQ(""); setShortcut("");
    };

    const toggle = (n) => setCollapsed(c => ({ ...c, [n]: !c[n] }));

    // D5 — selection + bulk helpers.
    const toggleSelected = (pid) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(pid)) next.delete(pid); else next.add(pid);
            return next;
        });
    };
    const clearSelection = () => { setSelectedIds(new Set()); setBulkResult(null); };

    // After a single-row owner change: patch the local row so the
    // Owner cell + version reflect authority state without a full refetch.
    const applyOwnerChangeLocally = (pid, apiResult) => {
        setData((prev) => {
            const sections = (prev.sections || []).map((sec) => ({
                ...sec,
                projects: (sec.projects || []).map((p) => {
                    if (p.id !== pid) return p;
                    const admin = admins.find((a) => a.id === apiResult.new_owner_id);
                    return {
                        ...p,
                        owner_id: apiResult.new_owner_id,
                        owner_version: apiResult.new_owner_version,
                        owner_display_name: admin?.display_name || admin?.email || p.owner_display_name,
                    };
                }),
            }));
            return { ...prev, sections };
        });
    };

    const runBulkAssign = async () => {
        if (!bulkOwnerId || selectedIds.size === 0) return;
        setBulkRunning(true);
        setBulkResult(null);
        try {
            const r = await api.post("/admin/projects/bulk/owner", {
                project_ids: Array.from(selectedIds),
                new_owner_id: bulkOwnerId,
                reason: "bulk assignment from Projects table",
            });
            setBulkResult(r.data);
            toast.success(`${r.data.ok_count} assigned · ${r.data.failed_count} failed`);
            // Refresh the whole list so per-project OCC/version is fresh.
            const refresh = await api.get(`/admin/projects?${query}`);
            setData(refresh.data);
            setSelectedIds(new Set());
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Bulk assign failed");
        } finally {
            setBulkRunning(false);
        }
    };

    // Data-driven owner options (§6, §17 R). Deduped list of every
    // non-null owner_id observed in the current unfiltered response.
    // When no owners are assigned the list is empty — the filter still
    // renders with only the "any" option. NEVER invented.
    const ownerOptions = useMemo(() => {
        const s = new Set();
        for (const sec of (data.sections || [])) {
            for (const p of (sec.projects || [])) {
                if (p.owner_id) s.add(p.owner_id);
            }
        }
        return Array.from(s).sort();
    }, [data]);

    const s = data.summary || {};
    const gs = globalSummary || {};

    // Card click handler — §6: cards remain functional shortcuts. The
    // displayed NUMBER stays global (from `gs`); the click updates the
    // filtered view via `shortcut` (and, for Active Projects, also
    // ensures Project State = Active). Toggling the same card off
    // returns to the plain filter state.
    const onCardClick = (key) => {
        if (shortcut === key) {
            setShortcut("");
            return;
        }
        if (key === "active_projects") {
            setState("Active");
        }
        setShortcut(key);
    };

    return (
        <div className="flex flex-col gap-6" style={{ "--sticky-top": "132px" }} data-testid="projects-landing">
            <div className="flex items-start justify-between">
                <div>
                    <h1 className="font-manrope text-3xl font-semibold text-charcoal mb-1" data-testid="projects-page-title">Projects</h1>
                    <p className="text-[#666] text-[15px]">Projects grouped by current Workstage.</p>
                </div>
            </div>

            {/* Sticky summary shortcut cards (§5, §6 + Sticky Chrome
                Micro-fix §1). Container bleeds to the edges of the
                main content area via `-mx-6 md:-mx-10` (cancelling the
                admin main's page padding) and restores horizontal
                padding inside so the cards' inner alignment matches
                the rest of the page. Sticks at `top:0` within the
                viewport; a subtle bottom border keeps the sticky
                boundary legible; a solid Warm Off-White background
                prevents scrolling content underneath from bleeding
                through. Card NUMBER stays bound to `globalSummary`
                (unchanged from previous refinement). */}
            <div className="sticky top-0 z-20 -mx-6 md:-mx-10 px-6 md:px-10 py-4 bg-offwhite border-b border-[#E5E2DC]" data-testid="summary-cards-sticky">
                <div className="flex flex-wrap gap-4" data-testid="summary-cards">
                    {[
                        { key: "active_projects", label: "Active Projects", val: gs.active_projects, tone: "" },
                        { key: "new_enquiries", label: "New Enquiries", val: gs.new_enquiries, tone: "",
                            tooltip: "New enquiries where no email has yet been sent or phone call logged." },
                        { key: "attention_needed", label: "Attention Needed", val: gs.attention_needed, tone: "attention" },
                        { key: "overdue_next_actions", label: "Overdue Next Actions", val: gs.overdue_next_actions, tone: "attention" },
                    ].map(c => (
                        <button
                            key={c.key}
                            data-testid={`summary-${c.key}`}
                            data-value={c.val ?? 0}
                            title={c.tooltip || undefined}
                            onClick={() => onCardClick(c.key)}
                            className={`flex-1 min-w-[180px] text-left bg-white border rounded-lg p-4 transition hover:shadow-sm ${shortcut === c.key ? "border-charcoal" : "border-[#E5E2DC]"}`}>
                            <div className="text-[13px] text-[#666] font-medium mb-2">{c.label}</div>
                            <div className={`font-manrope text-2xl font-semibold ${c.tone === "attention" ? "text-terracotta" : "text-charcoal"}`}>{c.val ?? 0}</div>
                        </button>
                    ))}
                </div>
            </div>

            {/* D5 — Bulk owner assignment action bar. Appears when
                one or more rows are selected. Bulk endpoint invokes
                `stage5_ownership.assign_project_owner` per project. */}
            {selectedIds.size > 0 && (
                <div
                    className="sticky top-[100px] z-30 mx-0 mb-2 rounded-lg border border-terracotta/40 bg-[#FDF4F2] px-4 py-3 flex items-center gap-3 flex-wrap"
                    data-testid="bulk-owner-bar"
                >
                    <span className="text-[13px] font-semibold text-charcoal" data-testid="bulk-owner-selected-count">
                        {selectedIds.size} project{selectedIds.size === 1 ? "" : "s"} selected
                    </span>
                    <select
                        value={bulkOwnerId}
                        onChange={(e) => setBulkOwnerId(e.target.value)}
                        data-testid="bulk-owner-select"
                        className="border border-[#E5E2DC] rounded px-2 py-1 text-[13px] bg-white"
                    >
                        <option value="">Choose owner…</option>
                        {admins.map((a) => (
                            <option key={a.id} value={a.id}>
                                {a.display_name || a.email}
                            </option>
                        ))}
                    </select>
                    <button
                        type="button"
                        onClick={runBulkAssign}
                        disabled={!bulkOwnerId || bulkRunning}
                        data-testid="bulk-owner-apply"
                        className="px-3 py-1 rounded bg-terracotta text-white text-[13px] font-semibold disabled:opacity-40"
                    >
                        {bulkRunning ? "Assigning…" : "Assign Owner"}
                    </button>
                    <button
                        type="button"
                        onClick={clearSelection}
                        data-testid="bulk-owner-clear"
                        className="px-2 py-1 text-[12px] text-[#666] hover:text-charcoal"
                    >
                        Clear
                    </button>
                    {bulkResult && (
                        <div className="basis-full text-[12px] mt-2" data-testid="bulk-owner-result">
                            <span className="text-[#2E7D32] font-semibold">{bulkResult.ok_count} assigned</span>
                            {bulkResult.failed_count > 0 && (
                                <>
                                    {" · "}
                                    <span className="text-[#B71C1C] font-semibold">{bulkResult.failed_count} failed</span>
                                    <div className="mt-1 text-[11px] text-[#B71C1C]">
                                        {(bulkResult.results || []).filter((r) => !r.ok).slice(0, 8).map((r) => (
                                            <div key={r.project_id} data-testid={`bulk-owner-fail-${r.project_id}`}>
                                                · {r.project_id.slice(0, 8)}… — {r.error}
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Two-column layout (§1): Workstage-grouped list on the
                left, sticky filter sidebar on the right. Sidebar
                collapses beneath the main content on narrow widths so
                the projects table is never squashed. */}
            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-6 items-start" data-testid="projects-two-column">
                {/* LEFT — Workstage sections (§3, §8) */}
                <div className="min-w-0">
                    {loading ? (
                        <div className="text-[#666]">Loading projects…</div>
                    ) : (
                        <div className="flex flex-col gap-6" data-testid="projects-sections">
                            {WORKSTAGE_ORDER.map(({ num, name }) => {
                                const section = data.sections?.find(s => s.workstage === num) || { count: 0, projects: [] };
                                const isCollapsed = collapsed[num] === undefined ? section.count === 0 : collapsed[num];
                                // §Projects Created-At Extension — every
                                //   workstage now renders the "Created At"
                                //   column in place of "Lead Quality".
                                //   Client-side newest-first sort by
                                //   `created_at` is kept ONLY for WS1 + WS2
                                //   so the workflow-priority order
                                //   (`_row_ordering_key`: overdue →
                                //   attention → awaiting → due → last
                                //   activity) is preserved for WS3-WS7.
                                const sortByCreatedAt = num === 1 || num === 2;
                                const projectsInOrder = sortByCreatedAt
                                    ? section.projects.slice().sort((a, b) => {
                                        const av = a.created_at || "";
                                        const bv = b.created_at || "";
                                        if (av === bv) return 0;
                                        return av < bv ? 1 : -1;   // desc
                                      })
                                    : section.projects;
                                return (
                                    <div key={num} className={`flex flex-col gap-3 ${isCollapsed ? "opacity-70" : ""}`} data-testid={`workstage-section-${num}`}>
                                        <button onClick={() => toggle(num)} className="flex items-center gap-3 px-4 py-3 bg-[#EBE8E0] hover:bg-[#E2DFD6] rounded-md text-left transition" data-testid={`workstage-header-${num}`}>
                                            <ChevronDown className={`w-4 h-4 transition-transform ${isCollapsed ? "-rotate-90" : ""}`} />
                                            <span className="font-manrope font-semibold text-charcoal text-[15px]">{num}. {name}</span>
                                            <span className="ml-auto text-[13px] text-[#666]" data-testid={`workstage-count-${num}`}>{section.count} {section.count === 1 ? "project" : "projects"}</span>
                                        </button>
                                        {!isCollapsed && section.projects.length > 0 && (
                                            <div className="bg-white border border-[#E5E2DC] rounded-lg overflow-x-auto">
                                                <table className="w-full text-[13px] text-left whitespace-nowrap">
                                                    <thead className="text-[#666] font-normal">
                                                        <tr>
                                                            <th className="w-[36px] py-3 px-3 border-b border-[#E5E2DC]" />
                                                            <th className="font-medium py-3 px-4 border-b border-[#E5E2DC]">Client</th>
                                                            <th className="font-medium py-3 px-4 border-b border-[#E5E2DC]">Project</th>
                                                            <th className="font-medium py-3 px-4 border-b border-[#E5E2DC]">Budget</th>
                                                            <th className="font-medium py-3 px-4 border-b border-[#E5E2DC]">Owner</th>
                                                            <th className="font-medium py-3 px-4 border-b border-[#E5E2DC]">Created At</th>
                                                            <th className="font-medium py-3 px-4 border-b border-[#E5E2DC]">Next Task</th>
                                                            <th className="font-medium py-3 px-4 border-b border-[#E5E2DC]">Next Client Action</th>
                                                            <th className="font-medium py-3 px-4 border-b border-[#E5E2DC]">Attention</th>
                                                            <th className="font-medium py-3 px-4 border-b border-[#E5E2DC]">Last Activity</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {projectsInOrder.map(p => (
                                                            <tr key={p.id} data-testid={`project-row-${p.id}`} onClick={() => navigate(`/admin/projects/${p.id}`)} className="cursor-pointer hover:bg-[#FAFAFA] border-t border-[#E5E2DC]">
                                                                <td
                                                                    className="py-3 px-3 align-middle"
                                                                    onClick={(e) => { e.stopPropagation(); toggleSelected(p.id); }}
                                                                    data-testid={`project-select-${p.id}`}
                                                                >
                                                                    {selectedIds.has(p.id)
                                                                        ? <CheckSquare className="w-4 h-4 text-terracotta" />
                                                                        : <Square className="w-4 h-4 text-[#999]" />}
                                                                </td>
                                                                <td className="py-3 px-4 font-semibold text-charcoal">
                                                                    {p.client_name || "—"}
                                                                    {/* V1 §7 — concise First-Contact indicator, WS1 only. */}
                                                                    {p.workstage === 1 && (
                                                                        <div className="text-[11px] font-normal mt-1"
                                                                             data-testid={`first-contact-${p.id}`}>
                                                                            {p.first_contact_status === "contact_made"
                                                                                ? (<span className="text-[#2E7D32]">Contacted · {fmtDate(p.first_contact_at)}</span>)
                                                                                : (<span className="text-terracotta font-semibold">Not contacted</span>)}
                                                                        </div>
                                                                    )}
                                                                </td>
                                                                <td className="py-3 px-4">
                                                                    <div className="font-medium">{(p.project_types || []).join(", ") || "—"}</div>
                                                                    <div
                                                                        className="text-[#666] text-[12px] mt-1 max-w-[280px] truncate"
                                                                        title={(p.property_address && p.property_address.trim()) || p.postcode || ""}
                                                                        data-testid={`project-address-${p.id}`}
                                                                    >
                                                                        {(p.property_address && p.property_address.trim())
                                                                            || p.postcode
                                                                            || "—"}
                                                                    </div>
                                                                    <div className="mt-1 flex items-center gap-2">
                                                                        <SourceBadge source={p.source} detail={p.source_detail} />
                                                                        <span data-testid={`project-state-${p.id}`}>
                                                                            <Pill tone={p.project_state === "Active" ? "active" : p.project_state === "On Hold" ? "hold" : "closed"}>{p.project_state}</Pill>
                                                                        </span>
                                                                    </div>
                                                                </td>
                                                                <td className="py-3 px-4 text-charcoal" data-testid={`project-budget-${p.id}`}>
                                                                    {BUDGET_DISPLAY[p.construction_budget] || "—"}
                                                                </td>
                                                                <td className="py-3 px-4">
                                                                    <OwnerCell project={p} admins={admins} onOwnerChanged={applyOwnerChangeLocally} />
                                                                </td>
                                                                <td className="py-3 px-4" data-testid={`project-created-at-${p.id}`}>
                                                                    {(() => {
                                                                        const c = fmtCreatedAt(p.created_at);
                                                                        if (!c) return <span className="text-[#666]">—</span>;
                                                                        return (
                                                                            <div>
                                                                                <div>{c.date}</div>
                                                                                <div className="text-[#666] text-[12px] mt-1">{c.time}</div>
                                                                            </div>
                                                                        );
                                                                    })()}
                                                                </td>
                                                                <td className="py-3 px-4">
                                                                    <NextItemCell item={p.next_task} testid={`project-next-task-${p.id}`} />
                                                                </td>
                                                                <td className="py-3 px-4">
                                                                    <NextItemCell item={p.next_client_action} testid={`project-next-client-action-${p.id}`} />
                                                                </td>
                                                                <td className="py-3 px-4"><AttentionCell a={p.attention} /></td>
                                                                <td className="py-3 px-4 text-[#666]">{fmtRel(p.last_activity_at)}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                        {!isCollapsed && section.projects.length === 0 && (
                                            <div className="text-[#999] text-[13px] px-4 py-2" data-testid={`workstage-empty-${num}`}>No projects in this Workstage under the current filters.</div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* RIGHT — Sticky filter sidebar (§1–§4). Search
                    relocated here (§2); pill groups follow the
                    suggested order. This is a Projects-page filter
                    panel only — NOT the B2 project-context sidebar. */}
                <aside
                    className="lg:sticky lg:self-start bg-white border border-[#E5E2DC] rounded-lg p-4 flex flex-col gap-3"
                    style={{
                        top: "var(--sticky-top)",
                        maxHeight: "calc(100vh - var(--sticky-top) - 1rem)",
                        overflowY: "auto",
                    }}
                    data-testid="projects-filter-sidebar">
                    <div className="flex items-center justify-between">
                        <h2 className="font-manrope text-[13px] font-semibold uppercase tracking-wide text-[#666]">Filters</h2>
                        <button data-testid="filter-clear" onClick={clearFilters} className="text-[12px] text-[#666] hover:text-charcoal underline">Clear filters</button>
                    </div>
                    <div className="flex items-center gap-2 border border-[#E5E2DC] rounded-md px-3 py-2">
                        <Search className="w-4 h-4 text-[#666]" />
                        <input data-testid="filter-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Search client, postcode, description…" className="outline-none w-full text-[13px]" />
                    </div>
                    <div className="flex flex-col gap-3" data-testid="projects-filters">
                        <RadioPillGroup testid="filter-project-state" label="Project State" value={state} setValue={setState}
                                  options={[{v:"Active",l:"Active"},{v:"On Hold",l:"On Hold"},{v:"Closed",l:"Closed"}]} />
                        {closedSelected && (
                            <PillGroup
                                testid="filter-closure-outcome"
                                label="Closure Outcome"
                                values={closureOutcome}
                                setValues={setClosureOutcome}
                                options={outcomeOptions.map(o => ({v:o.code,l:o.label}))}
                                emptyHint="No configured outcomes"
                            />
                        )}
                        {/* Owner filter — DATA-DRIVEN from
                            authoritative project.owner_id, never
                            invented (§17 R). When no owners are
                            assigned, the group shows only the empty
                            hint. */}
                        <PillGroup testid="filter-owner" label="Owner" values={owner} setValues={setOwner}
                                  options={ownerOptions.map(o => {
                                      const a = admins.find(x => x.id === o);
                                      return {v: o, l: (a && (a.display_name || a.email)) || o};
                                  })}
                                  emptyHint="No assigned owners yet" />
                        <PillGroup testid="filter-attention" label="Attention" values={attention} setValues={setAttention}
                                  options={[{v:"with",l:"Attention Needed"},{v:"without",l:"No Attention Needed"}]} />
                        <PillGroup testid="filter-quality" label="Lead Quality" values={quality} setValues={setQuality}
                                  options={[{v:"High",l:"High"},{v:"Medium",l:"Medium"},{v:"Low",l:"Low"}]} />
                        <PillGroup testid="filter-source" label="Source" values={source} setValues={setSource}
                                  options={[{v:"Meta",l:"Meta"},{v:"Website",l:"Website"},{v:"Manual",l:"Manual"}]} />
                        <PillGroup testid="filter-budget" label="Budget" values={budget} setValues={setBudget}
                                  options={BUDGET_OPTIONS.map(o => ({v: o.value, l: o.label_compact}))} />
                        <PillGroup testid="filter-project-type" label="Project Type" values={ptype} setValues={setPtype}
                                  options={[{v:"rear_extension",l:"Rear extension"},{v:"side_extension",l:"Side extension"},{v:"loft",l:"Loft"},{v:"internal_reconfiguration",l:"Internal reconfig."},{v:"combination",l:"Combination"},{v:"other",l:"Other"}]} />
                        {/* V1 §6 — First Contact filter. Radio: "" |
                            "none" | "made". Ranked immediately after
                            Project State visually since operators need
                            it primarily for new WS1 enquiries. */}
                        <RadioPillGroup testid="filter-first-contact" label="First Contact" value={firstContact} setValue={setFirstContact}
                                  options={[{v:"none",l:"No initial contact"},{v:"made",l:"Contact made"}]} />
                    </div>
                </aside>
            </div>
        </div>
    );
}

// Inline single-select radio pill group — used exclusively by Project
// State per the B1 Closure Outcome follow-up clarification. Visual
// style matches `PillGroup`; interaction is a radiogroup — at most one
// value active. Clicking the active pill toggles it OFF (returns to
// "no Project State filter"); clicking a different pill replaces the
// current selection. ARIA: `role="radiogroup"` on the container,
// `role="radio"` + `aria-checked` on each pill.
function RadioPillGroup({ testid, label, value, setValue, options }) {
    const onClick = (v) => {
        if (value === v) {
            // Toggle-off: clicking the currently active pill clears
            // the selection. This is a natural affordance and does not
            // conflict with the WAI-ARIA radiogroup pattern for this
            // filter surface — the value is a query filter, not form
            // input, so an "unset" state is meaningful.
            setValue("");
        } else {
            setValue(v);
        }
    };
    return (
        <div className="flex flex-col gap-2 py-1"
             role="radiogroup"
             aria-label={label}
             data-testid={testid}>
            <span className="text-[11px] font-manrope font-semibold uppercase tracking-wide text-[#666]">
                {label}
            </span>
            <div className="flex flex-wrap items-center gap-2">
                {options.map(o => {
                    const checked = value === o.v;
                    return (
                        <button
                            key={o.v}
                            type="button"
                            role="radio"
                            aria-checked={checked}
                            data-testid={`${testid}-opt-${o.v}`}
                            onClick={() => onClick(o.v)}
                            className={`px-2.5 py-1 rounded-full text-[12px] border transition select-none ${
                                checked
                                    ? "bg-charcoal text-white border-charcoal shadow-sm"
                                    : "bg-white text-charcoal border-[#E5E2DC] hover:border-charcoal/60"
                            }`}>
                            {o.l}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

// Inline multi-select pill/chip filter — B1 R2 clarification. Renders
// EVERY option visibly on the page as a toggle chip (no popover / no
// dropdown). Users may select multiple within the same group. Selected
// state is visually obvious via a solid charcoal background. Groups
// wrap across rows naturally when they exceed available width.
function PillGroup({ testid, label, values, setValues, options, allowEmpty = true, emptyHint }) {
    const toggle = (v) => {
        if (values.includes(v)) {
            const next = values.filter(x => x !== v);
            if (next.length === 0 && !allowEmpty) return;
            setValues(next);
        } else {
            setValues([...values, v]);
        }
    };
    return (
        <div className="flex flex-col gap-2 py-1" data-testid={testid}>
            <span className="text-[11px] font-manrope font-semibold uppercase tracking-wide text-[#666]">
                {label}
            </span>
            {options.length === 0 ? (
                <span className="text-[12px] text-[#999] italic" data-testid={`${testid}-empty`}>
                    {emptyHint || "No values"}
                </span>
            ) : (
                <div className="flex flex-wrap items-center gap-2">
                    {options.map(o => {
                        const checked = values.includes(o.v);
                        return (
                            <button
                                key={o.v}
                                type="button"
                                role="switch"
                                aria-pressed={checked}
                                data-testid={`${testid}-opt-${o.v}`}
                                onClick={() => toggle(o.v)}
                                className={`px-2.5 py-1 rounded-full text-[12px] border transition select-none ${
                                    checked
                                        ? "bg-charcoal text-white border-charcoal shadow-sm"
                                        : "bg-white text-charcoal border-[#E5E2DC] hover:border-charcoal/60"
                                }`}>
                                {o.l}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

